import type { Observable } from 'rxjs';
import {
  takeUntil,
  share,
  mergeMap,
  shareReplay,
  scan,
  distinctUntilChanged,
  from,
  mergeAll,
  toArray,
  map,
  filter,
  merge,
  iif,
  of,
  bufferCount,
  last,
  reduce,
  catchError,
  EMPTY,
} from 'rxjs';
import { cloneDeep, uniq } from 'lodash';

import {
  isHotelIngredientResponse,
  isFlightIngredientResponse,
  isFerryIngredientResponse,
  isAvailableHotelIngredientResponse,
  isOwnStayIngredientResponse,
  isAvailableActivityIngredientResponse,
  isTransportIngredientResponse,
} from '@libs/search-service-smart-search';
import {
  IngredientType,
  AvailabilityOrder,
} from '@libs/trip-planner-interfaces';
import type {
  HotelIngredientAdapter,
  ItineraryWithPrice,
  OverviewStaticInfoResult,
  ItinerarySearchCriteria,
  ItineraryResponse,
  ItineraryMainIngredientResponse,
  HeartedHotel,
  ActivityIngredientAdapter,
  IngredientId,
  OwnStayIngredientAdapter,
  HotelIngredientResponse,
  OwnStayIngredientResponse,
  Money,
  ItinerarySuggestionPrices,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';
import {
  isDefined,
  logged,
  ingredientDateComparer,
  retryHandled,
} from '@libs/utils';
import {
  trackItineraryHotelStaticInfoRetriesStarted,
  trackItineraryHotelStaticInfoRetriesSucceeded,
} from '@libs/feature-toggle';

import type { SmartSearchService } from '../search/smartSearchService';
import {
  isHotelIngredientAdapter,
  isOwnStayIngredientAdapter,
} from '../recipe/ingredients';

type IngredientProductCodes = {
  hotelCodes: string[];
  activityCodes: string[];
};

type HeartedHotelsByIngredient = Map<IngredientId, HeartedHotel[]>;
type ActivitiesByIngredient = Map<IngredientId, ActivityIngredientAdapter[]>;
type ProductCodesByIngredient = Map<IngredientId, IngredientProductCodes>;

export class ItinerarySearchResults {
  public readonly itineraries: Observable<ItineraryWithPrice>;
  public readonly cheapestItinerary: Observable<ItineraryWithPrice>;
  public readonly staticInfo: Observable<OverviewStaticInfoResult>;
  public readonly mostPopularItinerarySuggestions: Observable<ItinerarySuggestionPrices>;
  public readonly cheapestItinerarySuggestions: Observable<ItinerarySuggestionPrices>;
  public readonly mostPopularItinerarySuggestionPrice: Observable<Money | null>;
  public readonly cheapestItinerarySuggestionPrice: Observable<Money | null>;
  private uniqueProductCodesByIngredient = new Map<IngredientId, Set<string>>();

  constructor(
    searchService: SmartSearchService,
    public criteria: ItinerarySearchCriteria,
    cancel: Observable<unknown>
  ) {
    criteria.userCriteria.ingredients?.forEach((ingredient) =>
      this.uniqueProductCodesByIngredient.set(
        ingredient.ingredientId,
        new Set()
      )
    );

    const searchResults = searchService
      .searchItineraries(criteria)
      .pipe(takeUntil(cancel), share());

    this.itineraries = searchResults.pipe(
      mergeMap((itineraryResponse) => this.flattenItinerary(itineraryResponse)),
      logged('itineraries'),
      shareReplay()
    );

    const itinerarySuggestions = this.itineraries.pipe(
      last(),
      catchError(() => EMPTY),
      map(() => searchService.searchItinerarySuggestions(criteria)),
      share()
    );

    this.mostPopularItinerarySuggestions = itinerarySuggestions.pipe(
      mergeMap((suggestions) => suggestions.mostPopularSuggestedItineraries)
    );

    this.mostPopularItinerarySuggestionPrice = this.getCheapestItinerary(
      this.mostPopularItinerarySuggestions
    ).pipe(
      map(
        (mostPopularItinerarySuggestion) =>
          mostPopularItinerarySuggestion.totalPrice ??
          mostPopularItinerarySuggestion.totalPriceWithAlternative
      )
    );

    this.cheapestItinerarySuggestions = itinerarySuggestions.pipe(
      mergeMap((suggestions) => suggestions.cheapestSuggestedItineraries)
    );

    this.cheapestItinerarySuggestionPrice = this.getCheapestItinerary(
      this.cheapestItinerarySuggestions
    ).pipe(
      map(
        (mostPopularItinerarySuggestion) =>
          mostPopularItinerarySuggestion.totalPrice ??
          mostPopularItinerarySuggestion.totalPriceWithAlternative
      )
    );

    this.cheapestItinerary = this.getCheapestItinerary(this.itineraries);
    this.staticInfo = this.getStaticInfo(criteria, searchService);
  }

  private flattenItinerary({
    key,
    date,
    itinerary,
    prices,
    isSearched,
  }: ItineraryResponse): Observable<ItineraryWithPrice> {
    return from(itinerary).pipe(
      mergeAll(),
      toArray(),
      map<ItineraryMainIngredientResponse[], ItineraryWithPrice>(
        (ingredients) => {
          const itinerary = cloneDeep(ingredients);

          itinerary.map((ingredient) => {
            if (
              isHotelIngredientResponse(ingredient) ||
              isOwnStayIngredientResponse(ingredient)
            ) {
              ingredient.subIngredientResponse.sort((a, b) =>
                ingredientDateComparer(a, b, isTransportIngredientResponse)
              );
            }

            return ingredient;
          });

          const ownStayOnlyItinerary = itinerary.filter(
            isOwnStayIngredientResponse
          );
          const hotelOnlyItinerary = itinerary.filter(
            isHotelIngredientResponse
          );
          const flightOnlyItinerary = itinerary.filter(
            isFlightIngredientResponse
          );
          const ferryOnlyItinerary = itinerary.filter(
            isFerryIngredientResponse
          );

          const isPartial = itinerary.some((response) => {
            if (
              isAvailableHotelIngredientResponse(response) ||
              isOwnStayIngredientResponse(response)
            ) {
              return response.subIngredientResponse.some(
                (subIngredient) => !subIngredient.isAvailable
              );
            }

            return !response.isAvailable;
          });

          const isOnlyOwnStay = itinerary.every(
            (response) =>
              isOwnStayIngredientResponse(response) &&
              !response.subIngredientResponse.length
          );

          return {
            key,
            date,
            totalPrice: isPartial || isOnlyOwnStay ? null : prices.totalPrice,
            totalPriceWithAlternative: prices.totalPriceWithAlternative,
            grossPrice: prices.grossPrice,
            grossPriceWithAlternative: prices.grossPriceWithAlternative,
            cheapestPrice: null,
            mostPopularPrice: null,
            itinerary,
            ownStayOnlyItinerary,
            hotelOnlyItinerary,
            flightOnlyItinerary,
            ferryOnlyItinerary,
            isSearched,
          };
        }
      ),
      filter(
        (itineraryWithPrice) =>
          itineraryWithPrice.itinerary.length === itinerary.length
      )
    );
  }

  private getCheapestItinerary(
    itineraries: Observable<ItineraryWithPrice>
  ): Observable<ItineraryWithPrice>;
  private getCheapestItinerary(
    itineraries: Observable<ItinerarySuggestionPrices>
  ): Observable<ItinerarySuggestionPrices>;
  private getCheapestItinerary(
    itineraries: Observable<ItineraryWithPrice | ItinerarySuggestionPrices>
  ): Observable<ItineraryWithPrice | ItinerarySuggestionPrices> {
    const cheapestCompleteItinerary$ = itineraries.pipe(
      filter((itinerary) => !!itinerary.totalPrice),
      scan((cheapestItinerary, currentItinerary) => {
        if (!currentItinerary.totalPrice) {
          return cheapestItinerary;
        }

        if (!cheapestItinerary.totalPrice) {
          return currentItinerary;
        }

        const isCurrentItineraryCheaper =
          currentItinerary.totalPrice.amount <
          cheapestItinerary.totalPrice.amount;

        const hasCurrentItinerarySamePrice =
          currentItinerary.totalPrice.amount ===
          cheapestItinerary.totalPrice.amount;

        const isCurrentItineraryEarlier = new SemboDate(
          currentItinerary.date
        ).isBefore(cheapestItinerary.date);

        if (
          isCurrentItineraryCheaper ||
          (hasCurrentItinerarySamePrice && isCurrentItineraryEarlier)
        ) {
          return currentItinerary;
        }

        return cheapestItinerary;
      }),
      distinctUntilChanged((previousItinerary, currentItinerary) => {
        const isSameAmount =
          previousItinerary.totalPrice?.amount ===
          currentItinerary.totalPrice?.amount;
        const isSameDate = new SemboDate(previousItinerary.date).isSame(
          currentItinerary.date
        );

        return isSameAmount && isSameDate;
      }),
      shareReplay()
    );

    const cheapestAlternativeItinerary$ = itineraries.pipe(
      filter((itinerary) => !!itinerary.totalPriceWithAlternative),
      reduce((cheapestItinerary, currentItinerary) => {
        if (!currentItinerary.totalPriceWithAlternative) {
          return cheapestItinerary;
        }

        if (!cheapestItinerary.totalPriceWithAlternative) {
          return currentItinerary;
        }

        const isCurrentItineraryCheaper =
          currentItinerary.totalPriceWithAlternative.amount <
          cheapestItinerary.totalPriceWithAlternative.amount;

        const hasCurrentItinerarySamePrice =
          currentItinerary.totalPriceWithAlternative.amount ===
          cheapestItinerary.totalPriceWithAlternative.amount;

        const isCurrentItineraryEarlier = new SemboDate(
          currentItinerary.date
        ).isBefore(cheapestItinerary.date);

        if (
          isCurrentItineraryCheaper ||
          (hasCurrentItinerarySamePrice && isCurrentItineraryEarlier)
        ) {
          return currentItinerary;
        }

        return cheapestItinerary;
      }),
      distinctUntilChanged((previousItinerary, currentItinerary) => {
        const isSameAmount =
          previousItinerary.totalPriceWithAlternative?.amount ===
          currentItinerary.totalPriceWithAlternative?.amount;
        const isSameDate = new SemboDate(previousItinerary.date).isSame(
          currentItinerary.date
        );

        return isSameAmount && isSameDate;
      }),
      shareReplay()
    );

    const firstUnavailableItinerary$ = itineraries.pipe(
      filter(
        (itinerary) =>
          !itinerary.totalPrice && !itinerary.totalPriceWithAlternative
      ),
      reduce((previousItinerary, currentItinerary) => {
        if (
          new SemboDate(currentItinerary.date).isBefore(previousItinerary.date)
        ) {
          return currentItinerary;
        }

        return previousItinerary;
      })
    );

    return merge(
      cheapestCompleteItinerary$,
      cheapestAlternativeItinerary$.pipe(takeUntil(cheapestCompleteItinerary$)),
      firstUnavailableItinerary$.pipe(
        takeUntil(
          merge(cheapestCompleteItinerary$, cheapestAlternativeItinerary$)
        )
      )
    );
  }

  private getStaticInfo(
    criteria: ItinerarySearchCriteria,
    searchService: SmartSearchService
  ): Observable<OverviewStaticInfoResult> {
    const hotelOnlyIngredients =
      criteria.userCriteria.ingredients?.filter(isHotelIngredientAdapter) ?? [];
    const ownStayOnlyIngredients =
      criteria.userCriteria.ingredients?.filter(isOwnStayIngredientAdapter) ??
      [];

    const recipeProductCodesStaticInfoAdapters: HotelIngredientAdapter[] =
      this.getRecipeProductCodesStaticInfoAdapters(
        hotelOnlyIngredients,
        ownStayOnlyIngredients
      );

    const relevantItineraryStaticInfoAdapters: Observable<
      HotelIngredientAdapter[]
    > = this.getRelevantItineraryStaticInfoAdapters(
      [...hotelOnlyIngredients, ...ownStayOnlyIngredients],
      criteria.userCriteria.selectedDate
    );

    const remainingItinerariesStaticInfoAdapters: Observable<
      HotelIngredientAdapter[]
    > = this.getItinerariesStaticInfoAdapters(this.itineraries, [
      ...hotelOnlyIngredients,
      ...ownStayOnlyIngredients,
    ]);

    const staticInfoAdapters: Observable<HotelIngredientAdapter[]> = merge(
      of(recipeProductCodesStaticInfoAdapters),
      relevantItineraryStaticInfoAdapters,
      remainingItinerariesStaticInfoAdapters
    ).pipe(filter((adapters) => adapters.length > 0));

    const MAX_RETRIES = 2;
    return staticInfoAdapters.pipe(
      mergeMap((itineraryAdapters) =>
        searchService.searchOverviewStaticInfo(itineraryAdapters).pipe(
          retryHandled({
            count: MAX_RETRIES,
            delay: 1000,
            onStart: trackItineraryHotelStaticInfoRetriesStarted,
            onSuccess: trackItineraryHotelStaticInfoRetriesSucceeded,
          })
        )
      ),
      share()
    );
  }

  private getRecipeProductCodesStaticInfoAdapters(
    hotelOnlyIngredients: HotelIngredientAdapter[],
    ownStayOnlyIngredients: OwnStayIngredientAdapter[]
  ): HotelIngredientAdapter[] {
    const heartedHotelsByIngredient: HeartedHotelsByIngredient =
      getHeartedHotelCodesByIngredient(hotelOnlyIngredients);

    const activitiesByIngredient: ActivitiesByIngredient =
      getActivityCodesByIngredient(
        [...hotelOnlyIngredients, ...ownStayOnlyIngredients],
        this.uniqueProductCodesByIngredient
      );

    const destinationProductsIngredientIds = uniq([
      ...heartedHotelsByIngredient.keys(),
      ...activitiesByIngredient.keys(),
    ]);

    if (destinationProductsIngredientIds.length === 0) {
      return [];
    }

    return destinationProductsIngredientIds.map((ingredientId) => ({
      constraint: {
        boardBasis: [],
        selectedHotelTypes: [],
      },
      heartedHotels: heartedHotelsByIngredient.get(ingredientId) ?? [],
      subIngredients: activitiesByIngredient.get(ingredientId) ?? [],
      ingredientId,
      ingredientType: IngredientType.Hotel,
      suggestedBy: AvailabilityOrder.None,
    }));

    function getHeartedHotelCodesByIngredient(
      hotelOnlyIngredients: HotelIngredientAdapter[]
    ): HeartedHotelsByIngredient {
      return hotelOnlyIngredients.reduce(
        (heartedHotelsByIngredient, hotelIngredient) => {
          if (hotelIngredient.heartedHotels.length > 0) {
            heartedHotelsByIngredient.set(
              hotelIngredient.ingredientId,
              hotelIngredient.heartedHotels
            );
          }
          return heartedHotelsByIngredient;
        },
        new Map<IngredientId, HeartedHotel[]>()
      );
    }

    function getActivityCodesByIngredient(
      stayOnlyIngredients: (
        | HotelIngredientAdapter
        | OwnStayIngredientAdapter
      )[],
      uniqueProductCodesByIngredient: Map<IngredientId, Set<string>>
    ): ActivitiesByIngredient {
      return stayOnlyIngredients.reduce(
        (activitiesByIngredient, stayIngredient) => {
          if (stayIngredient.subIngredients.length > 0) {
            activitiesByIngredient.set(
              stayIngredient.ingredientId,
              stayIngredient.subIngredients
            );

            const ingredientUniqueCodes = uniqueProductCodesByIngredient.get(
              stayIngredient.ingredientId
            );
            stayIngredient.subIngredients
              .map((subIngredient) => subIngredient.code)
              .forEach((subIngredientCode) =>
                ingredientUniqueCodes?.add(subIngredientCode)
              );
          }

          return activitiesByIngredient;
        },
        new Map<IngredientId, ActivityIngredientAdapter[]>()
      );
    }
  }

  private getRelevantItineraryStaticInfoAdapters(
    stayOnlyIngredients: (HotelIngredientAdapter | OwnStayIngredientAdapter)[],
    selectedDate?: Date
  ): Observable<HotelIngredientAdapter[]> {
    const selectedItinerary = this.itineraries.pipe(
      filter(
        (itinerary) =>
          itinerary.date.toISOString() === selectedDate?.toISOString()
      )
    );

    const relevantItinerary = iif(
      () => isDefined(selectedDate),
      selectedItinerary,
      this.cheapestItinerary
    );

    return this.getItinerariesStaticInfoAdapters(
      relevantItinerary,
      stayOnlyIngredients,
      { yieldEveryItinerary: true }
    );
  }

  private getItinerariesStaticInfoAdapters(
    itineraries: Observable<ItineraryWithPrice>,
    stayOnlyIngredients: (HotelIngredientAdapter | OwnStayIngredientAdapter)[],
    options?: {
      yieldEveryItinerary?: boolean;
    }
  ): Observable<HotelIngredientAdapter[]> {
    const itinerariesProductCodes: Observable<ProductCodesByIngredient> =
      itineraries.pipe(
        map((itinerary) =>
          this.getItineraryProductCodes([
            ...itinerary.hotelOnlyItinerary,
            ...itinerary.ownStayOnlyItinerary,
          ])
        ),
        filter((itineraryProductCodes) => itineraryProductCodes.size > 0)
      );

    return iif(
      () => !!options?.yieldEveryItinerary,
      itinerariesProductCodes.pipe(bufferCount(1)),
      itinerariesProductCodes.pipe(toArray())
    ).pipe(
      map((bufferedItineraryProductCodes) =>
        bufferedItineraryProductCodes.reduce(
          (
            totalItinerariesProductCodes: ProductCodesByIngredient,
            itineraryProductCodes
          ) => {
            Array.from(itineraryProductCodes.entries()).forEach(
              ([ingredientId, { activityCodes, hotelCodes }]) => {
                const currentIngredientProductCodes =
                  totalItinerariesProductCodes.get(ingredientId);

                currentIngredientProductCodes?.activityCodes.push(
                  ...activityCodes
                );
                currentIngredientProductCodes?.hotelCodes.push(...hotelCodes);
              }
            );

            return totalItinerariesProductCodes;
          },
          new Map<IngredientId, IngredientProductCodes>(
            stayOnlyIngredients.map<[IngredientId, IngredientProductCodes]>(
              (ingredient) => [
                ingredient.ingredientId,
                { hotelCodes: [], activityCodes: [] },
              ]
            )
          )
        )
      ),
      map((totalProductCodes) =>
        Array.from(totalProductCodes.entries()).reduce(
          (
            adapters: HotelIngredientAdapter[],
            [ingredientId, { activityCodes, hotelCodes }]
          ) => {
            if (activityCodes.length + hotelCodes.length > 0) {
              adapters.push(
                this.createHotelIngredientAdapter(
                  stayOnlyIngredients,
                  hotelCodes,
                  activityCodes,
                  ingredientId
                )
              );
            }

            return adapters;
          },
          []
        )
      ),
      share()
    );
  }

  private getItineraryProductCodes(
    itinerary: ItineraryMainIngredientResponse[]
  ): ProductCodesByIngredient {
    return itinerary.reduce((productCodesByIngredient, ingredientResponse) => {
      const hotelCodes: string[] = isHotelIngredientResponse(ingredientResponse)
        ? this.getHotelIngredientResponseCodes(ingredientResponse)
        : [];
      const activityCodes: string[] =
        isHotelIngredientResponse(ingredientResponse) ||
        isOwnStayIngredientResponse(ingredientResponse)
          ? this.getStayIngredientResponseActivityCodes(ingredientResponse)
          : [];

      if (hotelCodes.length || activityCodes.length) {
        productCodesByIngredient.set(ingredientResponse.ingredientId, {
          hotelCodes,
          activityCodes,
        });
      }

      return productCodesByIngredient;
    }, new Map<IngredientId, IngredientProductCodes>());
  }

  private getHotelIngredientResponseCodes(
    ingredientResponse: HotelIngredientResponse
  ): string[] {
    const ingredientUniqueCodes = this.uniqueProductCodesByIngredient.get(
      ingredientResponse.ingredientId
    );

    const hotelCodes: string[] = [];

    if (isAvailableHotelIngredientResponse(ingredientResponse)) {
      const suggestedHotelProductCode = ingredientResponse.suggested.code;

      this.addUniqueCode(
        suggestedHotelProductCode,
        hotelCodes,
        ingredientUniqueCodes
      );

      ingredientResponse.suggestions.forEach((suggestion) => {
        const suggestionHotelProductCode = suggestion.code;

        this.addUniqueCode(
          suggestionHotelProductCode,
          hotelCodes,
          ingredientUniqueCodes
        );
      });
    } else if (ingredientResponse.alternativeSuggestion) {
      const alternativeSuggestedHotelCode =
        ingredientResponse.alternativeSuggestion.code;

      this.addUniqueCode(
        alternativeSuggestedHotelCode,
        hotelCodes,
        ingredientUniqueCodes
      );
    }

    return hotelCodes;
  }

  private getStayIngredientResponseActivityCodes(
    ingredientResponse: HotelIngredientResponse | OwnStayIngredientResponse
  ): string[] {
    const ingredientUniqueCodes = this.uniqueProductCodesByIngredient.get(
      ingredientResponse.ingredientId
    );

    const activityCodes: string[] = [];

    ingredientResponse.subIngredientResponse.forEach(
      (activitySubIngredient) => {
        if (isAvailableActivityIngredientResponse(activitySubIngredient)) {
          const suggestedActivityCode = activitySubIngredient.suggested.code;

          this.addUniqueCode(
            suggestedActivityCode,
            activityCodes,
            ingredientUniqueCodes
          );
        } else if (activitySubIngredient.alternativeSuggestion) {
          const alternativeSuggestedActivityCode =
            activitySubIngredient.alternativeSuggestion.code;

          this.addUniqueCode(
            alternativeSuggestedActivityCode,
            activityCodes,
            ingredientUniqueCodes
          );
        }
      }
    );

    return activityCodes;
  }

  private addUniqueCode(
    code: string,
    ingredientCodes: string[],
    ingredientUniqueCodes?: Set<string>
  ): void {
    if (!ingredientUniqueCodes?.has(code)) {
      ingredientCodes.push(code);
      ingredientUniqueCodes?.add(code);
    }
  }

  private createHotelIngredientAdapter(
    stayOnlyIngredients: (HotelIngredientAdapter | OwnStayIngredientAdapter)[],
    hotelCodes: string[],
    activityCodes: string[],
    ingredientId: IngredientId
  ): HotelIngredientAdapter {
    const currentStayIngredient = stayOnlyIngredients.find(
      (stay) => stay.ingredientId === ingredientId
    );

    if (!currentStayIngredient) {
      throw new Error('HotelIngredient criteria not found for specified');
    }

    return {
      ingredientId: currentStayIngredient.ingredientId,
      ingredientType: IngredientType.Hotel,
      suggestedBy: AvailabilityOrder.Popular,
      constraint: {
        boardBasis: [],
        selectedHotelTypes: [],
        hotelCodes,
        maxNumberOfHotels: hotelCodes.length,
      },
      subIngredients: activityCodes.map((activityCode) => ({
        code: activityCode,
        ingredientType: IngredientType.Activity,
        offset: 0,
        ingredientId: currentStayIngredient.ingredientId,
        tickets: [],
      })),
      heartedHotels: [],
    };
  }
}
